import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateVoteReponseDto,
  GetVoteCountByIdeaResponseDto,
  UserHasVotedOnIdeaResponseDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';

export const useVotesApi = (api: AxiosInstance) => {
  const baseUrl = `/votes`;

  const userHasVotedOnIdea = async (ideaId: number): Promise<UserHasVotedOnIdeaResponseDto> => {
    if (!ideaId) return { data: { hasVoted: false } };
    const { data } = await api.get(`${baseUrl}/user-has-voted/${ideaId}`);
    return { data };
  };

  const createVote = async (ideaId: number): Promise<CreateVoteReponseDto> => {
    const { data } = await api.post(`${baseUrl}/${ideaId}`);
    return { data };
  };

  const getVoteCountByIdea = async (ideaId: number): Promise<GetVoteCountByIdeaResponseDto> => {
    const { data } = await api.get(`${baseUrl}/${ideaId}`);
    return { data };
  };

  const removeVote = async (ideaId: number) => {
    await api.delete(`${baseUrl}/${ideaId}`);
    return ideaId;
  };

  const queryClient = useQueryClient();

  const useGetCount = (ideaId: number) =>
    useQuery({
      queryKey: [baseUrl, ideaId],
      queryFn: () => getVoteCountByIdea(ideaId),
    });

  const useUserHasVoted = (ideaId: number) =>
    useQuery({
      queryKey: [baseUrl, 'user-has-voted', ideaId],
      queryFn: () => userHasVotedOnIdea(ideaId),
    });

  const useCreate = () =>
    useErrorHandlingMutation({
      mutationFn: createVote,
      onSuccess: async ({ data }) => {
        await queryClient.invalidateQueries({
          queryKey: [baseUrl, data.ideaId],
        });
        await queryClient.invalidateQueries({
          queryKey: [baseUrl, 'user-has-voted', data.ideaId],
        });
      },
    });

  const useRemove = () =>
    useErrorHandlingMutation({
      mutationFn: removeVote,
      onSuccess: async (ideaId) => {
        await queryClient.invalidateQueries({ queryKey: [baseUrl, ideaId] });
        await queryClient.invalidateQueries({
          queryKey: [baseUrl, 'user-has-voted', ideaId],
        });
      },
    });

  return { useUserHasVoted, useCreate, useGetCount, useRemove };
};
